/**
 * PWA Utility Functions
 *
 * In this Vite app we cannot render a true server-side dynamic `/manifest.json`
 * endpoint from the frontend alone. The manifest link must stay stable for
 * Chrome installability; changing it at runtime can suppress the browser's
 * install UI after a refresh. We therefore keep `/manifest.json` static and
 * only update runtime metadata like title, favicon, and install modal content.
 */

/** localStorage key for name/logo used at install time */
export const PWA_INSTALL_META_KEY = 'pwa_install_meta';
export const PWA_MANIFEST_PATH = '/manifest.json';
export const PWA_LOGO_PATH = '/upload/tradinglogo.png';
export const PWA_DEFAULT_NAME = 'اپلیکیشن';

export function buildPwaLogoUrl(cacheSeed = Date.now()) {
  return `${PWA_LOGO_PATH}?v=${encodeURIComponent(String(cacheSeed))}`;
}

function withFreshLogoCache(url, cacheSeed = Date.now()) {
  if (!url) {
    return buildPwaLogoUrl(cacheSeed);
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set('v', String(cacheSeed));

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return parsedUrl.toString();
    }

    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    const cleanUrl = url.split('?')[0];
    return `${cleanUrl}?v=${encodeURIComponent(String(cacheSeed))}`;
  }
}

/**
 * Absolute URL for the default app icon in public/upload
 */
export function getPublicUploadLogoUrl() {
  const base = import.meta.env.BASE_URL || '/';
  const path = `${base}${withFreshLogoCache(PWA_LOGO_PATH)}`.replace(/\/{2,}/g, '/');
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${normalized}`;
}

/** Resolve logo URL; falls back to upload/tradinglogo.png */
function resolveLogoUrl(logoUrl) {
  if (logoUrl) {
    const freshUrl = withFreshLogoCache(logoUrl);
    if (freshUrl.startsWith('http://') || freshUrl.startsWith('https://')) return freshUrl;
    if (freshUrl.startsWith('/')) return `${window.location.origin}${freshUrl}`;
    return `${window.location.origin}/${freshUrl}`;
  }
  return getPublicUploadLogoUrl();
}

/**
 * Check if the app is running as a PWA (installed)
 */
export function isPWAInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
}

export function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

/**
 * Persist app name + logo for runtime UI fallback.
 */
export function savePwaInstallMeta(settings) {
  if (!settings) return;
  try {
    localStorage.setItem(PWA_INSTALL_META_KEY, JSON.stringify({
      app_name: settings.app_name || '',
      app_logo: settings.app_logo || '',
      savedAt: Date.now(),
    }));
  } catch {
    /* ignore quota */
  }
}

/**
 * Read saved install meta
 * @returns {{ app_name: string, app_logo: string, savedAt?: number } | null}
 */
export function getPwaInstallMeta() {
  try {
    const raw = localStorage.getItem(PWA_INSTALL_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      app_name: typeof parsed.app_name === 'string' ? parsed.app_name : '',
      app_logo: typeof parsed.app_logo === 'string' ? parsed.app_logo : '',
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Merge live settings with localStorage fallback
 */
export function mergeInstallSettings(settings) {
  const ls = getPwaInstallMeta();
  return {
    app_name: settings?.app_name || ls?.app_name || PWA_DEFAULT_NAME,
    app_logo: withFreshLogoCache(settings?.app_logo || ls?.app_logo || PWA_LOGO_PATH),
  };
}

/**
 * Update Apple touch icon for iOS
 */
export function updateAppleTouchIcon(logoUrl) {
  const href = resolveLogoUrl(logoUrl);
  if (!href) return;

  try {
    const existingIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (existingIcon) {
      existingIcon.remove();
    }

    const link = document.createElement('link');
    link.rel = 'apple-touch-icon';
    link.href = href;
    document.head.appendChild(link);
  } catch (error) {
    console.error('Error updating Apple touch icon:', error);
  }
}

/**
 * Update browser tab favicon from logo URL
 */
export function updateFavicon(logoUrl) {
  const base = resolveLogoUrl(logoUrl);
  if (!base) return;

  try {
    const faviconRels = ['icon', 'shortcut icon'];

    faviconRels.forEach((relValue) => {
      let link = document.querySelector(`link[rel="${relValue}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = relValue;
        document.head.appendChild(link);
      }

      link.type = 'image/png';
      link.href = base;
    });
  } catch (error) {
    console.error('Error updating favicon:', error);
  }
}

/**
 * Update PWA-related document metadata (title, favicon, apple-touch-icon).
 *
 * Does NOT touch <link rel="manifest"> — see file-level comment.
 */
export async function updatePWAManifest(settings) {
  if (!settings) return;

  const { app_logo, app_name } = settings;

  if (app_name) {
    document.title = app_name;
  }

  updateAppleTouchIcon(app_logo);
  updateFavicon(app_logo);

  if (app_name) {
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitleMeta) {
      appleTitleMeta.content = app_name.length > 12 ? app_name.slice(0, 11) + '\u2026' : app_name;
    }
  }
}
